import React, {useCallback, useEffect, useState} from 'react'
import {Stack, StackProps, useMultiStyleConfig, Box, BoxProps, Flex, Text, TextProps, Link} from '@chakra-ui/react'
import {Button, ButtonProps, Icon} from '_uiComponents'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {useCustomApi, useViewport, useTencentCaptcha, useOpenDrawer} from '_customHooks'
import {FormFactory, FormValues} from '_factories'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import {ResponseStep1, Step1SubscribeForm} from '_types'
import {getAbsoluteUrl} from '_utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGlobalConfigState} from '_context'

interface NewsletterRegistrationProps {
    title?: string
    description?: string
    ctaText?: string
    localeID: string
    emailFromFooter?: string
    onSubmit: (data: ResponseStep1) => void
    onLoading: (loading: boolean) => void
}

/**
 * @name NewsletterRegistrationStep1
 *
 * @component
 *
 * @description
 * Renders step 1 of Newsletter Modal.
 *
 * @param {NewsletterRegistrationProps} props - Properties object
 * @param {string} [props.title] - Title for the locale modal.
 * @param {string} [props.description] - Description for the locale modal.
 * @param {string} [props.ctaText] - cta text from Page Designer.
 * @param {string} props.localeID - Locale id format it-it.
 * @param {string} [props.emailFromFooter] - Email from footer.
 * @param {function} props.onSubmit - Handle submit callback.
 * @param {function} props.onLoading - Handle loading callback.
 *
 * @returns {React.FC<NewsletterRegistrationProps>} Rendered Newsletter Modal Step 1.
 */
const NewsletterRegistrationStep1: React.FC<NewsletterRegistrationProps> = ({title, description, ctaText, localeID, emailFromFooter, onSubmit, onLoading}) => {
    const styles = useMultiStyleConfig('NewsletterModal_step_1') as Record<string, StackProps & TextProps & BoxProps>
    const {formatMessage} = useIntl()
    const {useVerifyNewsletterEmail} = useCustomApi()
    const {mutate} = useVerifyNewsletterEmail()
    const {openLoginDrawer} = useOpenDrawer()
    const {executeRecaptcha} = useGoogleReCaptcha()
    const {data: customer} = useCurrentCustomer()
    const {appPreferences} = useGlobalConfigState()
    const {hostLink, PhoneLogin} = appPreferences
    const {site} = useMultiSite()
    const {isDesktop} = useViewport()
    const {isTencentEnabled, isTencentReady, tencentCaptchaResult, triggerTencentCaptcha, resetTencentCaptcha} = useTencentCaptcha()

    const [token, setToken] = useState<string>('')
    const [serverError, setServerError] = useState<string | null>('')

    const {
        control,
        formState: {errors},
        handleSubmit,
        setValue,
        getValues,
    } = useForm<FormValues>({mode: 'onBlur', shouldFocusError: true})

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            return
        }
        const googleReCaptchaToken = await executeRecaptcha('newsletterForm')
        if (token === '') setToken(googleReCaptchaToken)
    }, [executeRecaptcha])

    const openAccount = () => {
        if (customer?.isGuest) {
            if (!isDesktop) {
                window.location.href = getAbsoluteUrl(hostLink ?? '', localeID, '/login?rurl=1')
            } else {
                openLoginDrawer({
                    phonelogin: PhoneLogin,
                    hostlink: hostLink,
                })
            }
        } else {
            if (hostLink) {
                const hostLinkLocalized = JSON.parse(hostLink)[site.id]
                window.location.href = getAbsoluteUrl(hostLinkLocalized, localeID, '/account')
            }
        }
    }

    const triggerTencentValidation = () => {
        setServerError(null)
        if (isTencentReady) {
            triggerTencentCaptcha()
        } else {
            setServerError('Captcha failed or was cancelled. Please try again.')
        }
    }

    const submitForm = (data) => {
        const {email} = data
        const {randstr} = data
        setServerError(null)
        try {
            onLoading(true)
            mutate(
                {email: email, tokenRecaptcha: token, randstr: randstr},
                {
                    onSuccess: (data) => {
                        onLoading(false)
                        if (data.success && !data.isCustomer) {
                            onSubmit({email: data.email, userScore: data.userScore})
                            localStorage.setItem('cu', data.nwlCustomerCRMId || '')
                        } else {
                            setServerError('userExist')
                        }
                        setToken('')
                        resetTencentCaptcha()
                    },
                    onError: (error) => {
                        const err = error as Error
                        onLoading(false)
                        if (err.message === 'Internal Server Error') {
                            err.message = formatMessage({
                                id: 'error.500.generic',
                                defaultMessage: 'An error has occurred. Please try again later',
                            })
                        }
                        setServerError(err.message)
                        setToken('')
                        resetTencentCaptcha()
                    },
                },
            )
        } catch (error) {
            console.error('Error during step1 newsletterForm:', error)
        }
    }

    const Step1SubscribeForm: Step1SubscribeForm = {
        type: 'email',
        name: 'email',
        label: formatMessage({
            id: 'label.input.email.profile',
            defaultMessage: 'e-mail',
        }),
        placeholder: formatMessage(
            {
                id: 'label.field.placeholder',
                defaultMessage: 'Enter',
            },
            {
                0: formatMessage({
                    id: 'label.input.email.profile',
                    defaultMessage: 'e-mail',
                }),
            },
        ),
        defaultValue: emailFromFooter,
    }

    useEffect(() => {
        if (emailFromFooter && emailFromFooter !== '') {
            setValue('email', emailFromFooter, {shouldValidate: true})
        }
    }, [emailFromFooter])

    useEffect(() => {
        if (isTencentEnabled === true && tencentCaptchaResult) {
            if (token === '') {
                setToken(tencentCaptchaResult.ticket)
            } else {
                submitForm({email: getValues('email'), randstr: tencentCaptchaResult.randstr})
            }
        } else if (isTencentEnabled === false && token === '') {
            void handleReCaptchaVerify()
        }
    }, [tencentCaptchaResult, isTencentEnabled, token])

    return (
        <Stack
            {...styles.form}
            data-testid="email-input-nl"
            as="form"
            noValidate
            onSubmit={!isTencentEnabled ? handleSubmit(submitForm) : handleSubmit(triggerTencentValidation)}
        >
            <Box {...styles.titleWrapper}>
                <Text>{title}</Text>
            </Box>
            <Box {...styles.descriptionWrapper}>
                <Text>{description}</Text>
            </Box>
            <Box {...styles.mandatoryFieldsWrapper}>
                <FormattedMessage id="label.contact.form.required.fields" defaultMessage="I campi contrassegnati da asterisco (*) sono obbligatori" />
            </Box>
            <Box {...styles.inputWrapper}>
                <FormFactory
                    control={control}
                    errors={errors}
                    name={Step1SubscribeForm.name}
                    type={Step1SubscribeForm.type}
                    label={Step1SubscribeForm.label}
                    placeholder={Step1SubscribeForm.placeholder}
                    options={Step1SubscribeForm.options}
                    defaultValue={Step1SubscribeForm.defaultValue}
                />

                {serverError && (
                    <Box {...styles.errorMessageWrapper}>
                        {serverError === 'userExist' ? (
                            <FormattedMessage
                                id="label.newsletter.form.exist.customer"
                                defaultMessage="Error: Are you already registered? {button} to subscribe to the Newsletter"
                                values={{
                                    button: (
                                        <Button type="button" onClick={openAccount} variant="textLink" size="xs">
                                            <FormattedMessage id="label.newsletter.form.exist.customer.button" defaultMessage="Log into your personal area" />
                                        </Button>
                                    ),
                                }}
                            />
                        ) : (
                            <Text>{serverError}</Text>
                        )}
                    </Box>
                )}
            </Box>

            <Flex {...styles.footerWrapper}>
                <Button
                    {...(styles.button as ButtonProps)}
                    variant="primary"
                    size="newsletter"
                    type="submit"
                    isDisabled={!token && (!isTencentEnabled || !isTencentReady)}
                >
                    <Icon size="sm" name="IconEmail" />
                    {ctaText}
                </Button>
                <Box as="span" {...styles.footerCopyrightMessage}>
                    <FormattedMessage
                        id="label.newsletter.form.policy"
                        defaultMessage={`By entering your e-mail address, you agree to receive Brunello Cucinelli communications regarding the brand’s collections, news, and special content. For more information, see our {privacyFooterLink}.`}
                        values={{
                            privacyFooterLink: (
                                <Link className="cc-privacy-link" href={`/${localeID}/privacypolicy`} target="_blank" data-cs-override="" rel="noreferrer">
                                    {formatMessage({id: 'newsletter.registration.privacy.link', defaultMessage: 'Privacy Policy'})}
                                </Link>
                            ),
                        }}
                    />
                </Box>
                {!isTencentEnabled && (
                    <Box {...styles.recaptchaMessage}>
                        <FormattedMessage
                            id="label.newsletter.form.required.recaptcha.text"
                            defaultMessage={`This site is protected by reCAPTCHA and the Google {privacyLink} and {termsLink} apply.`}
                            values={{
                                privacyLink: (
                                    <Link
                                        className="cc-privacy-link"
                                        aria-label={formatMessage({
                                            id: 'newsletter.registration.privacy.arialabel',
                                            defaultMessage: 'Visit Google’s Privacy Policy',
                                        })}
                                        href="https://policies.google.com/privacy"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {formatMessage({id: 'newsletter.registration.privacy.link', defaultMessage: 'Privacy Policy'})}
                                    </Link>
                                ),
                                termsLink: (
                                    <Link
                                        className="cc-privacy-link"
                                        aria-label={formatMessage({
                                            id: 'newsletter.registration.service.arialabel',
                                            defaultMessage: 'Visit Google’s Terms of Service',
                                        })}
                                        href="https://policies.google.com/terms"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {formatMessage({id: 'newsletter.registration.service.link', defaultMessage: 'Terms of Service'})}
                                    </Link>
                                ),
                            }}
                        />
                    </Box>
                )}
            </Flex>
        </Stack>
    )
}

export {NewsletterRegistrationStep1}
